<template>
    <TitleFieldsFormSection v-if="programmings.length > 0">
        <transition
            enter-active-class="transition ease-linear duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-linear duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <AppWell class="mt-5">
                <AppCard>
                    <div class="p-4">
                        {{ $t('messages.add_station_activity_to_programming_session?') }}

                        <div class="md:grid md:grid-cols-12 md:gap-6">
                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$tc('messages.programmings', 1)" for="programming_id" />
                                    <FormSelect
                                        id="programming_id"
                                        v-model="programming_id"
                                        :options="programmings"
                                        class="mt-1"
                                    />
                                </div>
                            </div>
                            <div class="sm:col-span-6">
                                <div class="mt-1" v-if="sessions.length > 0">
                                    <FormLabel :value="$tc('messages.sessions', 1)" for="session_id" />
                                    <div class="flex items-center">
                                        <button
                                            type="button"
                                            @click="open = !open"
                                            class="border mt-1 block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md mt-1 w-full"
                                        >
                                            {{
                                                session
                                                    ? `${$tc('messages.sessions', 1)} ${session.text}`
                                                    : $t('messages.select')
                                            }}
                                        </button>
                                        <div class="shrink-0">
                                            <button
                                                @click="selected = null"
                                                :class="{ invisible: !selected }"
                                                class="text-gray-600"
                                                type="button"
                                                :title="$t('messages.remove')"
                                            >
                                                <svg
                                                    class="w-6 h-6 inline-block"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                                <span class="sr-only">{{ $t('messages.remove') }}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </AppCard>
            </AppWell>
        </transition>
    </TitleFieldsFormSection>
    <SlideOver :title="$tc('messages.sessions', 2)" :show="open" @close="open = false">
        <div class="mt-5">
            {{ $t('messages.select') }}
        </div>

        <fieldset>
            <legend class="sr-only">
                {{ $tc('messages.sessions', 2) }}
            </legend>
            <div class="space-y-4">
                <div v-for="session in sessions" :key="session.value">
                    <label
                        :class="[
                            session.value == selected ? 'border-transparent ring-2 ring-indigo-500' : 'border-gray-300',
                        ]"
                        class="mt-3 relative block bg-white border rounded-lg shadow-sm px-6 py-4 cursor-pointer sm:justify-between focus:outline-none"
                    >
                        <input
                            type="radio"
                            name="session"
                            v-model="selected"
                            :value="session.value"
                            class="sr-only"
                            aria-labelledby="server-size-1-label"
                            aria-describedby="server-size-1-description-0 server-size-1-description-1"
                        />
                        <div>
                            <div>
                                {{ session.text }}
                            </div>
                            <div>
                                <ul>
                                    <li v-for="activity in session.activities" :key="activity.id">
                                        {{ activity.title }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <!--
                          Active: "border", Not Active: "border-2"
                          Checked: "border-indigo-500", Not Checked: "border-transparent"
                        -->
                        <div
                            :class="[
                                session.value == selected ? 'border border-indigo-500' : 'border-2 border-transparent',
                            ]"
                            class="absolute -inset-px rounded-lg border-2 pointer-events-none"
                            aria-hidden="true"
                        ></div>
                    </label>
                </div>
            </div>
        </fieldset>
    </SlideOver>
</template>
<script>
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';

export default {
    name: 'StationProgrammingSessionSelector',
    components: {
        AppCard,
        AppWell,
        FormLabel,
        FormSelect,
        TitleFieldsFormSection,
    },
};
</script>
<script setup>
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import SlideOver from '@/components/SlideOver.vue';
const props = defineProps({
    modelValue: {},
    subjectId: {},
    programmingsSearchUrl: {},
    programmingsSessionSearchUrl: {},
});
let emit = defineEmits(['update:modelValue']);
const selected = useVModel(props, 'modelValue', emit);

const programming_id = ref(null);
const programmings = ref([]);

const fetchProgrammings = () => {
    axios
        .get(props.programmingsSearchUrl, {
            params: {
                subject_id: props.subjectId,
            },
        })
        .then((response) => {
            programmings.value = response.data.items;
        });
};

watch(
    () => props.subjectId,
    () => fetchProgrammings(),
    { immediate: true },
);

const sessions = ref([]);
const fetchSessions = () => {
    axios
        .get(props.programmingsSessionSearchUrl, {
            params: {
                programming_id: programming_id.value,
                subject_id: props.subjectId,
            },
        })
        .then((response) => {
            sessions.value = response.data.items;
        });
};
watch(programming_id, () => fetchSessions());
const open = ref(false);

const session = computed(() => {
    const enemy = sessions.value.find((s) => s.value == selected.value);

    return enemy ? enemy : null;
});
</script>
